/**
 * Main application entry point
 *
 * This file sets up the React application with all necessary providers and routing.
 * The application uses React Router for navigation and various context providers
 * for state management across the application.
 */
import React, { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { BrowserRouter as Router } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import i18n from './i18n';
import { cleanupCorruptedLocalStorage } from './utils/localStorageCleanup';
import { setupGlobalDOMErrorHandler } from './utils/domErrorHandler';
import { performanceMonitor } from './utils/performanceMonitor';
import { isDevelopment } from './utils/environment';
import { queryClient } from './lib/queryClient';
import { AuthProvider } from './lib/AuthContext';
import { FormDataProvider } from './lib/FormDataContext';
import { NavigationProvider } from './lib/NavigationContext';
import { OnboardingProvider } from './lib/OnboardingContext';
import { WorkspaceProvider } from './lib/WorkspaceContext';
import { ToastProvider } from './contexts/ToastContext';
import { LayoutProvider } from './contexts/LayoutContext';
import { useFirebaseAnalytics } from './hooks/useFirebaseAnalytics';
import { useAnalyticsUserContext } from './hooks/useAnalyticsUserContext';
import { usePageViewTracking } from './hooks/usePageViewTracking';
import { ReactDOMErrorBoundary } from './components/ErrorBoundary/ReactDOMErrorBoundary';
import { AppRoutesWithErrorBoundaries as AppRoutes } from './routes-with-error-boundaries';
import { initDatadog } from './monitoring/datadogClient';
import { initSentry } from './monitoring/sentryClient';
import '../tailwind.css';

try {
  initSentry();
} catch (err) {
  console.warn('Sentry bootstrap failed; continuing without telemetry', err);
}
try {
  initDatadog();
} catch (err) {
  console.warn('Datadog bootstrap failed; continuing without RUM', err);
}
// Clean up any corrupted localStorage data on app start
cleanupCorruptedLocalStorage();
// Setup global DOM error handling
setupGlobalDOMErrorHandler();
// Note: Critical metrics are now cleared only once per session to prevent accumulation
/**
 * Initialize the React application by mounting it to the DOM
 *
 * The application is wrapped in various providers to enable:
 * - Internationalization (i18n)
 * - Routing (React Router)
 * - Authentication state management
 * - Navigation state management
 * - Workspace management
 * - Onboarding flow management
 * - Form data management
 * - React Query for server state management
 * - Toast notifications
 */

/**
 * Component to track page views - must be inside Router
 */
const PageViewTracker = () => {
  usePageViewTracking();
  return null;
};

/**
 * Hooks that require providers (React Query / Auth) should live under them.
 */
const AnalyticsUserContextTracker = () => {
  useAnalyticsUserContext();
  return null;
};

const AppWrapper = () => {
  // Use Firebase Analytics hook
  useFirebaseAnalytics();

  // Track app mounting performance and optimize rendering
  React.useEffect(() => {
    // Optimize font loading and track performance
    import('./utils/renderOptimization')
      .then(({ optimizeFontLoading }) => {
        optimizeFontLoading();
      })
      .catch((error) => {
        console.warn('Failed to load render optimization:', error);
      });
    // Track performance (only in development)
    if (isDevelopment) {
      try {
        performanceMonitor.markAppMounted();
      } catch (error) {
        console.warn('Failed to initialize performance monitor:', error);
      }
    }
    // Preload critical images during idle time
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        import('./utils/renderOptimization')
          .then(({ ImageOptimizer }) => {
            ImageOptimizer.preloadImages(['/KDL_Logo.svg']);
          })
          .catch((error) => {
            console.warn('Failed to load render optimization:', error);
          });
      });
    }
  }, []);
  return (
    <ReactDOMErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <I18nextProvider i18n={i18n}>
          <Router>
            <PageViewTracker />
            <AuthProvider>
              <AnalyticsUserContextTracker />
              <NavigationProvider>
                <WorkspaceProvider>
                  <OnboardingProvider>
                    <FormDataProvider>
                      <LayoutProvider>
                        <ToastProvider>
                          <AppRoutes />
                        </ToastProvider>
                      </LayoutProvider>
                    </FormDataProvider>
                  </OnboardingProvider>
                </WorkspaceProvider>
              </NavigationProvider>
            </AuthProvider>
          </Router>
        </I18nextProvider>
      </QueryClientProvider>
    </ReactDOMErrorBoundary>
  );
};
// Register service worker for offline functionality and caching
if ('serviceWorker' in navigator && !import.meta.env.DEV) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        // Service Worker registered successfully
        // Log in development for debugging, track in production for monitoring
        if (isDevelopment) {
          console.info('Service Worker registered successfully with scope:', registration.scope);
        }
        // Track successful registration for monitoring (production)
        // Note: This helps monitor service worker health across deployments
      })
      .catch((error) => {
        if (isDevelopment) {
          console.error('Service Worker registration failed:', error);
        }
      });
  });
}
// Use StrictMode only in development to catch side effects
// Disable StrictMode in development if HMR is causing DOM issues
const isDevMode = import.meta.env.DEV;
const hasDOMErrors = localStorage.getItem('react-dom-errors') === 'true';
const enableStrictMode =
  isDevMode && !window.location.search.includes('no-strict') && !hasDOMErrors;
// Safely get the app element with error handling
const appElement = document.getElementById('app');
if (!appElement) {
  console.error('App element not found! Make sure there is an element with id="app" in the HTML.');
  // Create a fallback element if needed
  const fallbackElement = document.createElement('div');
  fallbackElement.id = 'app';
  document.body.appendChild(fallbackElement);
}
// Global error handlers are now set up in setupGlobalDOMErrorHandler()
try {
  createRoot(appElement as HTMLElement).render(
    enableStrictMode ? (
      <StrictMode>
        <AppWrapper />
      </StrictMode>
    ) : (
      <AppWrapper />
    ),
  );
} catch (error) {
  console.error('Failed to render React app:', error);
  // Show a fallback error message
  if (appElement) {
    appElement.innerHTML = `
      <div style="padding: 20px; text-align: center; font-family: Arial, sans-serif;">
        <h1>Application Error</h1>
        <p>Failed to load the application. Please refresh the page.</p>
        <button onclick="window.location.reload()" style="padding: 10px 20px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer;">
          Refresh Page
        </button>
      </div>
    `;
  }
}
